import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check,
  X,
  Printer,
  ChefHat,
  PackageCheck,
  Clock,
  MapPin,
  Phone,
  CreditCard,
  Loader2,
  Utensils,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Countdown } from "@/components/Countdown";
import RiderTracker from "@/components/RiderTracker";
import { inr, shortId, fmtDateTime, bucketOf } from "@/lib/format";

const PREP_CHIPS = [10, 15, 20, 25, 30, 45];

const STATUS_STYLE = {
  new: "bg-amber-100 text-amber-800 border-amber-300",
  preparing: "bg-blue-100 text-blue-800 border-blue-300",
  ready: "bg-violet-100 text-violet-800 border-violet-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

export default function OrderDetail({ order, busy, onAccept, onReject, onAdvance, onPrint }) {
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [prep, setPrep] = useState(20);
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState({});
  const [handedOver, setHandedOver] = useState(false);

  useEffect(() => {
    setAcceptOpen(false);
    setRejectOpen(false);
    setReason("");
    setPrep(order?.prep_time_min || 20);
    setHandedOver(localStorage.getItem(`handover:${order?.id}`) === "1");
    try {
      setChecked(JSON.parse(localStorage.getItem(`checklist:${order?.id}`) || "{}"));
    } catch {
      setChecked({});
    }
  }, [order?.id, order?.prep_time_min]);

  if (!order) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-10 text-muted-foreground">
        <Utensils className="h-10 w-10 mb-4 opacity-40" />
        <p className="font-medium">Select an order</p>
        <p className="text-sm">Pick an order from the list to view details and take action.</p>
      </div>
    );
  }

  const bucket = bucketOf(order);
  const s = (order.status || "").toLowerCase();

  const items = order.items || [];
  const showChecklist = bucket === "ready" && !handedOver;
  const checkedCount = items.filter((_, i) => checked[i]).length;
  const allChecked = items.length > 0 && checkedCount === items.length;
  const toggleCheck = (i) => {
    setChecked((prev) => {
      const next = { ...prev, [i]: !prev[i] };
      localStorage.setItem(`checklist:${order.id}`, JSON.stringify(next));
      return next;
    });
  };
  const confirmHandover = () => {
    setHandedOver(true);
    localStorage.setItem(`handover:${order.id}`, "1");
    toast.success("Order verified & handed to rider");
  };

  return (
    <motion.div
      key={order.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="h-full flex flex-col"
      data-testid="order-detail"
    >
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="font-display font-black text-2xl" data-testid="detail-order-id">
                {shortId(order.id)}
              </h2>
              <Badge
                variant="outline"
                className={cn("capitalize font-semibold", STATUS_STYLE[bucket])}
                data-testid="detail-status-badge"
              >
                {order.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {fmtDateTime(order.created_at)}
            </p>
            {order.customer_name ? (
              <p className="text-sm font-medium mt-1.5 flex items-center gap-1.5" data-testid="detail-customer">
                <User className="h-3.5 w-3.5 text-muted-foreground" /> {order.customer_name}
                {order.customer_phone ? (
                  <span className="text-muted-foreground font-normal">· {order.customer_phone}</span>
                ) : null}
              </p>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onPrint}
            data-testid="print-bill-btn"
            className="gap-1.5 shrink-0"
          >
            <Printer className="h-4 w-4" /> Print bill
          </Button>
        </div>

        {order.prep_time_min ? (
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm bg-accent text-accent-foreground rounded-md px-2.5 py-1 font-medium">
            <ChefHat className="h-3.5 w-3.5" /> Prep time: {order.prep_time_min} min
          </p>
        ) : null}
        {bucket === "preparing" ? <div><Countdown order={order} /></div> : null}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
        {/* Live delivery-partner tracking */}
        {bucket !== "new" && bucket !== "cancelled" && <RiderTracker order={order} />}

        {/* Items */}
        <div>
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-3">
            {showChecklist ? `Handover checklist · ${checkedCount}/${items.length} checked` : "Items"}
          </p>
          <div className="space-y-2.5">
            {items.map((it, i) => (
              <div key={`${it.menu_item_id || it.name}-${i}`} className={cn("flex items-center justify-between gap-3 transition-opacity", showChecklist && checked[i] && "opacity-50")} data-testid="detail-item-row">
                <div className="flex items-center gap-3 min-w-0">
                  {showChecklist ? (
                    <button
                      onClick={() => toggleCheck(i)}
                      data-testid={`check-item-${i}`}
                      aria-pressed={!!checked[i]}
                      className={cn(
                        "h-6 w-6 rounded-md border grid place-items-center shrink-0 transition-colors",
                        checked[i] ? "bg-primary border-primary text-white" : "border-input hover:border-primary"
                      )}
                    >
                      {checked[i] ? <Check className="h-4 w-4" /> : null}
                    </button>
                  ) : null}
                  {it.image ? (
                    <img src={it.image} alt="" className="h-9 w-9 rounded object-cover shrink-0" />
                  ) : null}
                  <span className="h-6 w-6 shrink-0 rounded bg-primary/10 text-primary text-xs font-bold grid place-items-center">
                    {it.quantity || 1}
                  </span>
                  <span className={cn("font-medium truncate", showChecklist && checked[i] && "line-through")}>{it.name}</span>
                </div>
                <span className="font-mono text-sm shrink-0">
                  {inr((it.price || 0) * (it.quantity || 1))}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bill */}
        <div className="rounded-md border border-border p-4 bg-muted/40">
          <div className="space-y-1.5 text-sm">
            {[
              ["Subtotal", order.subtotal],
              ["Delivery fee", order.delivery_fee],
              ["Platform fee", order.platform_fee],
              ["GST (Food)", order.gst_food],
              ["GST (Service)", order.gst_service],
            ]
              .filter(([, v]) => v != null && Number(v) !== 0)
              .map(([label, v]) => (
                <div key={label} className="flex justify-between text-muted-foreground">
                  <span>{label}</span>
                  <span className="font-mono">{inr(v)}</span>
                </div>
              ))}
            <div className="flex justify-between pt-2 mt-1 border-t border-border font-bold text-base">
              <span>Total</span>
              <span className="font-mono" data-testid="detail-total">
                {inr(order.total ?? order.gross_total)}
              </span>
            </div>
            {order.vendor_payout != null && (
              <div className="flex justify-between pt-1 text-primary font-semibold">
                <span>Your payout</span>
                <span className="font-mono">{inr(order.vendor_payout)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2.5">
            <CreditCard className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-muted-foreground text-xs">Payment</p>
              <p className="font-medium capitalize">
                {order.payment_method || "—"}
                <span
                  className={cn(
                    "ml-2 text-xs font-semibold",
                    order.payment_status === "paid" ? "text-primary" : "text-amber-600"
                  )}
                >
                  {(order.payment_status || "").toUpperCase()}
                </span>
              </p>
            </div>
          </div>
          {order.delivery_address && (
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Deliver to</p>
                <p className="font-medium">{order.delivery_address}</p>
              </div>
            </div>
          )}
          {order.distance_km != null && (
            <div className="flex items-start gap-2.5">
              <Phone className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Distance</p>
                <p className="font-medium">{order.distance_km} km</p>
              </div>
            </div>
          )}
        </div>

        {order.instructions ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <span className="font-semibold text-amber-800">Customer note: </span>
            {order.instructions}
          </div>
        ) : null}
      </div>

      {/* Action bar */}
      <div className="p-4 sm:p-5 border-t border-border bg-card">
        {bucket === "new" && (
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-11 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              data-testid="reject-order-btn"
            >
              <X className="h-4 w-4 mr-1.5" /> Reject
            </Button>
            <Button
              className="flex-[2] h-11 font-semibold"
              onClick={() => setAcceptOpen(true)}
              disabled={busy}
              data-testid="accept-order-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="h-4 w-4 mr-1.5" /> Accept order</>)}
            </Button>
          </div>
        )}
        {bucket === "preparing" && (
          s === "accepted" ? (
            <Button className="w-full h-11 font-semibold" onClick={() => onAdvance("preparing")} disabled={busy} data-testid="mark-preparing-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><ChefHat className="h-4 w-4 mr-1.5" /> Start preparing</>)}
            </Button>
          ) : (
            <Button className="w-full h-11 font-semibold" onClick={() => onAdvance("ready")} disabled={busy} data-testid="mark-ready-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><PackageCheck className="h-4 w-4 mr-1.5" /> Mark ready for pickup</>)}
            </Button>
          )
        )}
        {bucket === "ready" && (
          handedOver ? (
            <p className="text-center text-sm font-medium text-primary py-1.5 flex items-center justify-center gap-2" data-testid="handover-done">
              <PackageCheck className="h-4 w-4" /> Verified &amp; handed to rider
            </p>
          ) : (
            <div>
              <p className="text-xs text-center text-muted-foreground mb-2">
                Tick every item, then confirm handover · <span className="font-semibold text-foreground">{checkedCount}/{items.length}</span>
              </p>
              <Button
                className="w-full h-11 font-semibold"
                disabled={!allChecked}
                onClick={confirmHandover}
                data-testid="confirm-handover-btn"
              >
                <PackageCheck className="h-4 w-4 mr-1.5" />
                {allChecked ? "Confirm handover to rider" : `Check all items (${checkedCount}/${items.length})`}
              </Button>
            </div>
          )
        )}
        {(bucket === "completed" || bucket === "cancelled") && (
          <p className="text-center text-sm text-muted-foreground py-1.5 capitalize">
            Order {order.status}
          </p>
        )}
      </div>

      {/* Accept dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent data-testid="accept-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Set preparation time</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            How long will {shortId(order.id)} take to prepare?
          </p>
          <div className="grid grid-cols-3 gap-2 py-2">
            {PREP_CHIPS.map((v) => (
              <button
                key={v}
                onClick={() => setPrep(v)}
                data-testid={`prep-chip-${v}`}
                className={cn(
                  "h-14 rounded-md border font-semibold transition-colors",
                  prep === v
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:bg-muted"
                )}
              >
                {v}
                <span className="block text-[10px] font-normal opacity-70">min</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button
              onClick={() => onAccept(prep)}
              disabled={busy}
              data-testid="confirm-accept-btn"
              className="font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Accept · ${prep} min`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject order?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Let the customer know why {shortId(order.id)} can't be fulfilled.
          </p>
          <div className="flex flex-wrap gap-2 py-1">
            {["Out of stock", "Too busy", "Closing soon", "Item unavailable"].map((r) => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={cn(
                  "text-xs px-2.5 py-1.5 rounded-full border transition-colors",
                  reason === r ? "border-destructive bg-destructive/10 text-destructive" : "border-border hover:bg-muted"
                )}
                data-testid={`reject-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {r}
              </button>
            ))}
          </div>
          <Textarea
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => onReject(reason)}
              disabled={busy}
              data-testid="confirm-reject-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
